import type { LocalizedText } from "./types";

export interface CoachMessage {
  lessonId: string;
  trigger: "your_turn" | "preflop" | "postflop" | "hand_complete";
  condition?: (ctx: {
    position?: string;
    phase?: string;
    isPreflop?: boolean;
  }) => boolean;
  message: LocalizedText;
}

// ---------------------------------------------------------------------------
// Position helpers
// ---------------------------------------------------------------------------

const isLatePosition = (pos?: string) =>
  pos === "BTN" || pos === "CO";

const isEarlyPosition = (pos?: string) =>
  pos === "UTG" || pos === "UTG+1";

const isBlinds = (pos?: string) =>
  pos === "SB" || pos === "BB";

// ---------------------------------------------------------------------------
// Coach message library — grouped by lessonId
// ---------------------------------------------------------------------------

const COACH_MESSAGES: CoachMessage[] = [
  // =========================================================================
  // Level 1 — Foundations (1-3: Game Flow, 1-4: Positions)
  // =========================================================================

  // 1-3 Game Flow
  {
    lessonId: "1-3",
    trigger: "preflop",
    message: {
      en: "New hand! Remember the flow: preflop, flop, turn, river. Watch how the community cards appear.",
      es: "Nueva mano! Recuerda el flujo: preflop, flop, turn, river. Observa como aparecen las cartas comunitarias.",
    },
  },
  {
    lessonId: "1-3",
    trigger: "postflop",
    message: {
      en: "The flop is out. Three community cards at once — now you have 5 cards to work with.",
      es: "Salio el flop. Tres cartas comunitarias de golpe — ahora tienes 5 cartas para trabajar.",
    },
  },
  {
    lessonId: "1-3",
    trigger: "hand_complete",
    message: {
      en: "Hand over! Notice how the pot was awarded. Each hand follows the same flow.",
      es: "Mano terminada! Observa como se repartio el bote. Cada mano sigue el mismo flujo.",
    },
  },

  // 1-4 Positions
  {
    lessonId: "1-4",
    trigger: "your_turn",
    condition: (ctx) => ctx.position === "BTN",
    message: {
      en: "You're on the Button — the best position! You act last and see what everyone does first.",
      es: "Estas en el Button — la mejor posicion! Actuas ultimo y ves lo que hacen todos primero.",
    },
  },
  {
    lessonId: "1-4",
    trigger: "your_turn",
    condition: (ctx) => isBlinds(ctx.position),
    message: {
      en: "You're in the blinds. You've already invested chips — but you'll act out of position after the flop.",
      es: "Estas en las ciegas. Ya invertiste fichas — pero actuaras fuera de posicion despues del flop.",
    },
  },
  {
    lessonId: "1-4",
    trigger: "your_turn",
    condition: (ctx) => isEarlyPosition(ctx.position),
    message: {
      en: "Early position means many players act after you. Be cautious with your hand selection here.",
      es: "Posicion temprana significa que muchos jugadores actuan despues de ti. Se prudente con tu seleccion de manos.",
    },
  },

  // =========================================================================
  // Level 2 — Core Skills
  // =========================================================================

  // 2-0 Starting Hands
  {
    lessonId: "2-0",
    trigger: "preflop",
    message: {
      en: "Remember: play fewer hands, but play them aggressively. Quality over quantity!",
      es: "Recuerda: juega menos manos, pero juegalas con agresividad. Calidad sobre cantidad!",
    },
  },
  {
    lessonId: "2-0",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === true,
    message: {
      en: "Look at your two cards. Are they in the top 20% of hands? If not, folding is often correct.",
      es: "Mira tus dos cartas. Estan en el top 20% de manos? Si no, retirarse suele ser correcto.",
    },
  },
  {
    lessonId: "2-0",
    trigger: "hand_complete",
    message: {
      en: "Think about the hands that were played. Were they strong starting hands or marginal ones?",
      es: "Piensa en las manos que se jugaron. Eran manos iniciales fuertes o marginales?",
    },
  },

  // 2-1 Position = Power
  {
    lessonId: "2-1",
    trigger: "your_turn",
    condition: (ctx) => isEarlyPosition(ctx.position),
    message: {
      en: "You're UTG — the tightest position. Only play premium hands here: AA, KK, QQ, AKs.",
      es: "Estas UTG — la posicion mas cerrada. Solo juega manos premium aqui: AA, KK, QQ, AKs.",
    },
  },
  {
    lessonId: "2-1",
    trigger: "your_turn",
    condition: (ctx) => isLatePosition(ctx.position),
    message: {
      en: "Late position! You can widen your range here — more hands become profitable with position.",
      es: "Posicion tardia! Puedes ampliar tu rango — mas manos son rentables con posicion.",
    },
  },
  {
    lessonId: "2-1",
    trigger: "postflop",
    message: {
      en: "Position matters even more post-flop. Acting last gives you information advantage.",
      es: "La posicion importa aun mas post-flop. Actuar ultimo te da ventaja de informacion.",
    },
  },

  // 2-2 Betting for Value
  {
    lessonId: "2-2",
    trigger: "postflop",
    message: {
      en: "Did you hit the board? Think about value betting — make your opponents pay to see more cards.",
      es: "Conectaste con el board? Piensa en apostar por valor — haz que tus oponentes paguen para ver mas cartas.",
    },
  },
  {
    lessonId: "2-2",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === false,
    message: {
      en: "When you have a strong hand, bet for value. The goal is to get called by worse hands.",
      es: "Cuando tengas una mano fuerte, apuesta por valor. El objetivo es que te paguen con manos peores.",
    },
  },

  // 2-3 Pot Basics
  {
    lessonId: "2-3",
    trigger: "your_turn",
    message: {
      en: "Check the pot size before you act. Your bet should be relative to the pot — typically 50-75%.",
      es: "Mira el tamano del bote antes de actuar. Tu apuesta debe ser relativa al bote — normalmente 50-75%.",
    },
  },
  {
    lessonId: "2-3",
    trigger: "postflop",
    message: {
      en: "The pot is growing. Keep track of it — it determines the right bet size and your pot odds.",
      es: "El bote crece. Lleva la cuenta — determina el tamano correcto de apuesta y tus pot odds.",
    },
  },

  // 2-4 Defending Your Blinds
  {
    lessonId: "2-4",
    trigger: "your_turn",
    condition: (ctx) => isBlinds(ctx.position) && ctx.isPreflop === true,
    message: {
      en: "You're in the blinds facing a raise. Don't defend with junk — but don't over-fold either.",
      es: "Estas en las ciegas ante una subida. No defiendas con basura — pero tampoco te retires demasiado.",
    },
  },
  {
    lessonId: "2-4",
    trigger: "preflop",
    condition: (ctx) => isBlinds(ctx.position),
    message: {
      en: "The chips in the blind are already in the pot — they're not yours anymore. Decide fresh.",
      es: "Las fichas de la ciega ya estan en el bote — ya no son tuyas. Decide sin apego.",
    },
  },

  // 2-5 Limping vs Raising
  {
    lessonId: "2-5",
    trigger: "preflop",
    message: {
      en: "If a hand is worth playing, it's usually worth raising. Limping gives away initiative.",
      es: "Si una mano vale la pena jugarla, normalmente vale la pena subirla. Limpear regala la iniciativa.",
    },
  },
  {
    lessonId: "2-5",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === true,
    message: {
      en: "Think: raise or fold. Open-limping is almost always a mistake at a full table.",
      es: "Piensa: subir o retirarse. Open-limpear es casi siempre un error en una mesa llena.",
    },
  },

  // =========================================================================
  // Level 3 — Strategy Basics
  // =========================================================================

  // 3-0 TAG Style
  {
    lessonId: "3-0",
    trigger: "preflop",
    message: {
      en: "Play TAG: tight hand selection + aggressive betting. Few hands, maximum pressure.",
      es: "Juega TAG: seleccion cerrada de manos + apuestas agresivas. Pocas manos, maxima presion.",
    },
  },
  {
    lessonId: "3-0",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === true,
    message: {
      en: "TAG means raising when you enter. If you're just calling, ask yourself: should I even be in this hand?",
      es: "TAG significa subir cuando entras. Si solo igualas, preguntate: deberia estar en esta mano?",
    },
  },

  // 3-1 Preflop Ranges by Position
  {
    lessonId: "3-1",
    trigger: "your_turn",
    condition: (ctx) => isEarlyPosition(ctx.position) && ctx.isPreflop === true,
    message: {
      en: "UTG range: ~12% of hands. Only the strongest: high pairs, AK, AQs.",
      es: "Rango UTG: ~12% de manos. Solo las mas fuertes: parejas altas, AK, AQs.",
    },
  },
  {
    lessonId: "3-1",
    trigger: "your_turn",
    condition: (ctx) => ctx.position === "BTN" && ctx.isPreflop === true,
    message: {
      en: "Button range: ~40% of hands. You can open wide here — suited connectors, small pairs, all broadways.",
      es: "Rango del Button: ~40% de manos. Puedes abrir amplio — conectores suited, parejas pequenas, todas las broadway.",
    },
  },

  // 3-2 Continuation Betting
  {
    lessonId: "3-2",
    trigger: "postflop",
    message: {
      en: "You raised preflop — consider a continuation bet to maintain pressure. C-bet ~50-66% of pot.",
      es: "Subiste preflop — considera una apuesta de continuacion para mantener la presion. C-bet ~50-66% del bote.",
    },
  },
  {
    lessonId: "3-2",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === false,
    message: {
      en: "C-betting works because you showed strength preflop. Your opponent folds often enough to make it profitable.",
      es: "El c-bet funciona porque mostraste fuerza preflop. Tu oponente se retira suficiente para hacerlo rentable.",
    },
  },

  // 3-3 When to Fold After the Flop
  {
    lessonId: "3-3",
    trigger: "postflop",
    message: {
      en: "You'll miss the flop ~2/3 of the time. It's OK to give up — don't throw good chips after bad.",
      es: "Fallaras el flop ~2/3 de las veces. Esta bien rendirse — no tires fichas buenas detras de malas.",
    },
  },
  {
    lessonId: "3-3",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === false,
    message: {
      en: "Facing a bet with nothing? No shame in folding. Saving chips is just as important as winning them.",
      es: "Te apuestan y no tienes nada? No hay vergüenza en retirarse. Ahorrar fichas es tan importante como ganarlas.",
    },
  },

  // 3-4 Reading the Board
  {
    lessonId: "3-4",
    trigger: "postflop",
    message: {
      en: "Read the board texture: is it dry (disconnected) or wet (coordinated)? This changes your strategy.",
      es: "Lee la textura del board: es seco (desconectado) o mojado (coordinado)? Esto cambia tu estrategia.",
    },
  },
  {
    lessonId: "3-4",
    trigger: "your_turn",
    condition: (ctx) => ctx.phase === "flop",
    message: {
      en: "Dry board = c-bet more often. Wet board = be more cautious. The texture tells you who it favors.",
      es: "Board seco = c-bet mas seguido. Board mojado = se mas cauteloso. La textura te dice a quien favorece.",
    },
  },

  // 3-5 Introduction to 3-Betting
  {
    lessonId: "3-5",
    trigger: "preflop",
    message: {
      en: "Someone raised? With a strong hand, consider 3-betting! Size it to ~3x the open raise.",
      es: "Alguien subio? Con una mano fuerte, considera hacer 3-bet! Tamano de ~3x la subida.",
    },
  },
  {
    lessonId: "3-5",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === true,
    message: {
      en: "3-bet for value with premium hands. Occasionally 3-bet as a bluff with hands like A5s to stay balanced.",
      es: "3-bet por valor con manos premium. Ocasionalmente 3-bet como farol con manos como A5s para mantenerte equilibrado.",
    },
  },

  // 3-6 Playing Against Raises
  {
    lessonId: "3-6",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === true,
    message: {
      en: "Facing a raise? Ask: does my hand play well post-flop, or should I 3-bet or fold?",
      es: "Enfrentas una subida? Preguntate: mi mano juega bien post-flop, o deberia hacer 3-bet o retirarme?",
    },
  },
  {
    lessonId: "3-6",
    trigger: "preflop",
    condition: (ctx) => isBlinds(ctx.position),
    message: {
      en: "Don't defend your blinds with trash just because you already invested. Quality calls only.",
      es: "No defiendas tus ciegas con basura solo porque ya invertiste. Solo calls de calidad.",
    },
  },

  // =========================================================================
  // Level 4 — Poker Math
  // =========================================================================

  // 4-0 Counting Outs
  {
    lessonId: "4-0",
    trigger: "postflop",
    message: {
      en: "Count your outs! How many cards can improve your hand? Each out is roughly 2% equity on one street.",
      es: "Cuenta tus outs! Cuantas cartas pueden mejorar tu mano? Cada out es ~2% de equity en una calle.",
    },
  },
  {
    lessonId: "4-0",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === false,
    message: {
      en: "Flush draw = 9 outs. Open-ended straight draw = 8 outs. Gutshot = 4 outs. Know your numbers!",
      es: "Proyecto de color = 9 outs. Escalera abierta = 8 outs. Gutshot = 4 outs. Conoce tus numeros!",
    },
  },

  // 4-1 Rule of 2 and 4
  {
    lessonId: "4-1",
    trigger: "postflop",
    message: {
      en: "Quick math: multiply your outs by 4 on the flop (2 cards to come) or by 2 on the turn (1 card to come).",
      es: "Calculo rapido: multiplica tus outs por 4 en el flop (2 cartas por venir) o por 2 en el turn (1 carta).",
    },
  },
  {
    lessonId: "4-1",
    trigger: "your_turn",
    condition: (ctx) => ctx.phase === "turn",
    message: {
      en: "On the turn: outs x 2 = your approximate equity %. Simple and effective!",
      es: "En el turn: outs x 2 = tu equity aproximada en %. Simple y efectivo!",
    },
  },

  // 4-2 Pot Odds
  {
    lessonId: "4-2",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === false,
    message: {
      en: "Before calling, check your pot odds. Is the price right compared to your chance of improving?",
      es: "Antes de igualar, mira tus pot odds. El precio es justo comparado con tu probabilidad de mejorar?",
    },
  },
  {
    lessonId: "4-2",
    trigger: "postflop",
    message: {
      en: "Pot odds = amount to call / (pot + amount to call). Compare with your equity to decide.",
      es: "Pot odds = cantidad a igualar / (bote + cantidad a igualar). Compara con tu equity para decidir.",
    },
  },

  // 4-3 Equity & All-in
  {
    lessonId: "4-3",
    trigger: "preflop",
    message: {
      en: "All-in decisions? Think equity: AA vs KK is 80/20. AKs vs QQ is a coin flip. Know the matchups.",
      es: "Decisiones all-in? Piensa en equity: AA vs KK es 80/20. AKs vs QQ es un coin flip. Conoce los matchups.",
    },
  },
  {
    lessonId: "4-3",
    trigger: "your_turn",
    message: {
      en: "Equity isn't about one hand — it's about the long run. Make +EV decisions consistently.",
      es: "La equity no es sobre una mano — es sobre el largo plazo. Toma decisiones +EV consistentemente.",
    },
  },

  // 4-4 Implied Odds
  {
    lessonId: "4-4",
    trigger: "your_turn",
    condition: (ctx) => ctx.isPreflop === false,
    message: {
      en: "Bad pot odds? If you can win a big pot when you hit, implied odds may justify the call.",
      es: "Malas pot odds? Si puedes ganar un bote grande al conectar, las implied odds pueden justificar el call.",
    },
  },
  {
    lessonId: "4-4",
    trigger: "preflop",
    message: {
      en: "Small pair preflop? The 15x rule: call if the effective stack is at least 15x the bet. Set mining!",
      es: "Pareja pequena preflop? La regla de las 15x: iguala si el stack efectivo es al menos 15x la apuesta. Set mining!",
    },
  },

  // 4-5 Expected Value (EV)
  {
    lessonId: "4-5",
    trigger: "your_turn",
    message: {
      en: "Think in EV: (probability of winning x amount won) - (probability of losing x amount lost). Is this play +EV?",
      es: "Piensa en EV: (probabilidad de ganar x ganancia) - (probabilidad de perder x perdida). Esta jugada es +EV?",
    },
  },
  {
    lessonId: "4-5",
    trigger: "hand_complete",
    message: {
      en: "Win or lose, the right question is: was that decision +EV? Results vary, but good decisions pay off.",
      es: "Ganes o pierdas, la pregunta correcta es: esa decision fue +EV? Los resultados varian, pero las buenas decisiones pagan.",
    },
  },
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const messagesByLesson = new Map<string, CoachMessage[]>();

for (const msg of COACH_MESSAGES) {
  const arr = messagesByLesson.get(msg.lessonId) ?? [];
  arr.push(msg);
  messagesByLesson.set(msg.lessonId, arr);
}

/**
 * Returns all coach messages for a given lesson ID.
 * Returns an empty array if no messages are defined for that lesson.
 */
export function getCoachMessages(lessonId: string): CoachMessage[] {
  return messagesByLesson.get(lessonId) ?? [];
}

/**
 * Lesson concept labels for the completion summary.
 * Maps lessonId → a short description of what was practiced.
 */
export const LESSON_CONCEPTS: Record<string, LocalizedText> = {
  "1-3": { en: "game flow", es: "el flujo del juego" },
  "1-4": { en: "table positions", es: "las posiciones en la mesa" },
  "2-0": { en: "starting hand selection", es: "seleccion de manos iniciales" },
  "2-1": { en: "positional play", es: "juego posicional" },
  "2-2": { en: "value betting", es: "apuestas de valor" },
  "2-3": { en: "pot management", es: "gestion del bote" },
  "2-4": { en: "blind defense", es: "defensa de ciegas" },
  "2-5": { en: "raising vs limping", es: "subir vs limpear" },
  "3-0": { en: "the TAG style", es: "el estilo TAG" },
  "3-1": { en: "preflop ranges", es: "rangos preflop" },
  "3-2": { en: "continuation betting", es: "apuesta de continuacion" },
  "3-3": { en: "post-flop folding", es: "retirarse post-flop" },
  "3-4": { en: "board reading", es: "lectura del board" },
  "3-5": { en: "3-betting", es: "el 3-bet" },
  "3-6": { en: "playing against raises", es: "jugar contra subidas" },
  "4-0": { en: "counting outs", es: "contar outs" },
  "4-1": { en: "the rule of 2 and 4", es: "la regla del 2 y 4" },
  "4-2": { en: "pot odds", es: "pot odds" },
  "4-3": { en: "equity", es: "equity" },
  "4-4": { en: "implied odds", es: "implied odds" },
  "4-5": { en: "expected value", es: "valor esperado" },
};
